import * as Dfns from 'date-fns/fp';
import { useCallback, useEffect, useMemo, useState, JSX } from 'react';
import {
  ActualVsPredictedDataInitial,
  CptbData,
  DecayData,
  RoasData,
  SatData
} from '@blisspointmedia/bpm-types/dist/MMM';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { MmmFeatureNameResponse } from '@blisspointmedia/bpm-types/dist/MMMFeatureNames';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import {
  capitalizeWords,
  medianOrMean,
  MedianOrMean
} from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { TINUITI_TEST_HOME_SERVICES, TINUITI_TEST_HOME_SERVICES_PROXY } from '@Pages/MMM/mmm-utils';
import {
  findGroupByOptions,
  StageTwoFileData
} from '@Components/Charts/ModelResults/DataFormatters';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { ModelConfig, CompositionRow } from '../hroas-types';
import { ChannelPlatformTacticBrand as CPTB } from './ChannelPlatformTacticBrand';
import { ReturnOnAdSpend } from './ReturnOnAdSpend';
import {
  calculateNormalizedWeights as calculateNormalizedWeightsUtil,
  HolisticRcCsvRow,
  MEAN_METRIC,
  parseChannelColumn,
  parseChannelName,
  ParsedMediaChannel,
  transformHolisticRcToSatData,
  TTM_WEEKS
} from '../utils/hroasUtils';
import {
  ActualizedCsvRow,
  adaptSpendEffectShareRowsToActualizedCsvRows,
  adaptMddRowsToRoasData,
  isDecayPreloadSettled,
  useDecayCurvesLambdaData,
  useRunInputs,
  useSpendEffectShareCsvData,
  useHolisticRcData,
  useMarginalDecisionLambdaData,
  useModelOverviewLambdaData,
  useRunSlots
} from '../utils/useModelResultsData';
import { familySlotIds, Slot } from '../utils/slots';
import { useTwostageDecompositionData } from '../utils/twostageDecomposition';
import { applyRoasPipeline, SpendUniverseEntry } from '../utils/roasPipeline';
import { FeatureMapsByModelType } from '../utils/useFeatureMapping';
import { ModelOverview } from './ModelOverview';
import { RoasCompositionByRevenueStream } from './RoasCompositionByRevenueStream';
import { SatAndDecay } from './SaturationAndDecay';

const GROUP_KEY_SEPARATOR = '|||';
const NO_SLOTS: Slot[] = [];

interface Model {
  id: string;
  label: string;
  appLabel: string;
  enabled: boolean;
  weight: number;
}

interface ProcessedHolisticGroup {
  media_channel: string;
  estimate_type: string;
  raw_spend: number;
  actualized_spend: number;
  raw_effect_sum: number;
  actualized_effect_sum: number;
  raw_return_next_dollar_sum: number;
  actualized_return_next_dollar_sum: number;
  previous_qtr_mean_value: number;
  model_type: string;
  dark_last_quarter?: number;
}

const DARK_INPUT_SPEND_THRESHOLD = 0.01;
const METRIC_MEAN = 'mean';

interface ModelResultsProps {
  showActualizedData: boolean;
  disabledPlatform: boolean | undefined;
  groupBy: string;
  setGroupBy: (val: string) => void;
  groupByOptions: { label: string; value: string }[];
  setGroupByOptions: (options: { label: string; value: string }[]) => void;
  kpi: string;
  runId: string | null;
  date: string;
  models: Model[];
  onModelsChange?: (updatedModels: ModelConfig[]) => void;
  toolbarOutcomeVariable?: string;
  // Per-model-type feature_id -> pretty-name maps, warmed at the HROAS page level (keyed on
  // "rMMM"/"GeoMMM"/"BrandEquity"). HROAS blends rows from multiple model types simultaneously, so
  // SES/RC/MDD resolve each row against the map for its own model_type; undefined for old-format
  // clients / while a given map is still loading.
  featureMaps?: FeatureMapsByModelType;
  // Single representative feature map (the first enabled model's type), kept for the decay-curve
  // path, which only ever resolves against one model type at a time.
  featureMap?: MmmFeatureNameResponse;
}

export const ModelResults = ({
  disabledPlatform,
  groupBy,
  setGroupBy,
  groupByOptions,
  setGroupByOptions,
  kpi,
  runId,
  date,
  showActualizedData,
  models,
  onModelsChange,
  toolbarOutcomeVariable,
  featureMaps,
  featureMap
}: ModelResultsProps): JSX.Element => {
  const { cid: originalCompany } = useCompanyInfo();

  let { cid: company } = useCompanyInfo();
  if (TEST_COMPANIES.includes(company)) {
    company = 'test';
  } else if (company === TINUITI_TEST_HOME_SERVICES) {
    company = TINUITI_TEST_HOME_SERVICES_PROXY;
  }
  const stackedCsvQuery = useModelOverviewLambdaData(originalCompany, kpi, runId);
  const spendEffectShareCsvQuery = useSpendEffectShareCsvData(
    originalCompany,
    kpi,
    runId,
    featureMaps
  );
  const holisticDecayQuery = useDecayCurvesLambdaData(
    originalCompany,
    models,
    kpi,
    runId,
    featureMap
  );
  const runInputs = useRunInputs(originalCompany, kpi, runId);
  const holisticRcQuery = useHolisticRcData(originalCompany, kpi, runId, featureMaps);
  const mddQuery = useMarginalDecisionLambdaData(originalCompany, kpi, runId, featureMaps);

  const [cptbData, setCptbData] = useState<CptbData[]>([]);
  const [overallRoasCpa, setOverallRoasCpa] = useState<number>();
  const [roasData, setRoasData] = useState<RoasData[]>([]);
  const [roasDataDirect, setRoasDataDirect] = useState<RoasData[]>([]);
  const [satData, setSatData] = useState<SatData[]>([]);
  const [satDataDirect, setSatDataDirect] = useState<SatData[]>([]);
  const [twoStageData, setTwoStageData] = useState<StageTwoFileData[]>();
  const [omitDarkInputs, setOmitDarkInputs] = useState<boolean>(true);
  const [centralMeasure, setCentralMeasure] = useState<MedianOrMean>(MedianOrMean.MEDIAN);

  // The run's trained slots (shares the model-results bundle cache — no extra
  // request). Slot ids are the model_type vocabulary of every adapted row below.
  const { data: runSlots } = useRunSlots(originalCompany, kpi, runId);
  const slots = runSlots ?? NO_SLOTS;

  const modelLabels = useMemo<Record<string, string>>(
    () => Object.fromEntries(models.map(model => [model.id, model.appLabel])),
    [models]
  );

  const rmmmSlotIds = useMemo(() => familySlotIds(slots, 'rmmm'), [slots]);
  const brandEquitySlotIds = useMemo(() => familySlotIds(slots, 'brand_equity'), [slots]);
  const isBrandEquityModelType = useCallback(
    (modelType: string): boolean => brandEquitySlotIds.includes(modelType),
    [brandEquitySlotIds]
  );

  const enabledModelTypes = useMemo(
    () => models.filter(model => model.enabled).map(model => model.id),
    [models]
  );

  const twostageDecompositionQuery = useTwostageDecompositionData(
    originalCompany,
    kpi,
    runId,
    enabledModelTypes,
    featureMaps
  );

  const actualizedCsvDataAll = useMemo(
    (): ActualizedCsvRow[] =>
      spendEffectShareCsvQuery.data != null
        ? adaptSpendEffectShareRowsToActualizedCsvRows(spendEffectShareCsvQuery.data)
        : [],
    [spendEffectShareCsvQuery.data]
  );

  // Per-date set-intersection of the enabled models' mean actualized rows.
  // Null with <2 contributing models (nothing to intersect → no clipping).
  const sharedActualizedDates = useMemo((): Set<string> | null => {
    const datesByModelType = new Map<string, Set<string>>();
    actualizedCsvDataAll.forEach(row => {
      if (!enabledModelTypes.includes(row.model_type) || row.metric !== METRIC_MEAN) {
        return;
      }
      const rowDate = row.time || row.date || '';
      if (!rowDate) {
        return;
      }
      if (!datesByModelType.has(row.model_type)) {
        datesByModelType.set(row.model_type, new Set());
      }
      datesByModelType.get(row.model_type)!.add(rowDate);
    });
    const sets = Array.from(datesByModelType.values());
    if (sets.length <= 1) {
      return null;
    }
    return sets.reduce((acc, set) => new Set(Array.from(acc).filter(d => set.has(d))));
  }, [actualizedCsvDataAll, enabledModelTypes]);

  // Actualized SES clipped to the shared dates (holistic blends are only valid
  // where every enabled model has data). The raw historical-spend chart reads
  // spendEffectShareCsvQuery.data directly and stays unclipped.
  const actualizedCsvData = useMemo(
    (): ActualizedCsvRow[] =>
      sharedActualizedDates == null
        ? actualizedCsvDataAll
        : actualizedCsvDataAll.filter(row => sharedActualizedDates.has(row.time || row.date || '')),
    [actualizedCsvDataAll, sharedActualizedDates]
  );
  const holisticRcData = useMemo(() => holisticRcQuery.data ?? [], [holisticRcQuery.data]);

  // Map indexes for O(1) lookups over actualized data (avoids repeated full-array scans on toggle)
  // Same pattern as EfficientFrontier (aggregationMap, budgetMap, etc.)
  /** Rows keyed by media_channel; used in transformHolisticToRoasData to find csvRow per channel */
  const actualizedByMediaChannel = useMemo(() => {
    const m = new Map<string, ActualizedCsvRow[]>();
    actualizedCsvData.forEach(row => {
      if (!row.media_channel) {
        return;
      }
      const list = m.get(row.media_channel) ?? [];
      list.push(row);
      m.set(row.media_channel, list);
    });
    return m;
  }, [actualizedCsvData]);
  /** Rows keyed by channel; passed to calculateRoasFromActualizedData so it only scans that channel's rows */
  const actualizedByChannel = useMemo(() => {
    const m = new Map<string, ActualizedCsvRow[]>();
    actualizedCsvData.forEach(row => {
      if (row.channel == null) {
        return;
      }
      const list = m.get(row.channel) ?? [];
      list.push(row);
      m.set(row.channel, list);
    });
    return m;
  }, [actualizedCsvData]);

  const stackedCsvPreload = stackedCsvQuery.data ?? null;
  const holisticDecayDataPreload = holisticDecayQuery.data?.decayData ?? null;
  const decayStructurePreload = holisticDecayQuery.data?.decayStructure ?? null;

  const refreshDate = useMemo(
    () => stackedCsvPreload?.[0]?.refresh_date ?? '',
    [stackedCsvPreload]
  );

  // Empty: fed only to the legacy Excel export now that the mmm/ bundle is gone.
  const actualVsPredictedData = useMemo<ActualVsPredictedDataInitial[]>(() => [], []);
  const decayData = useMemo<DecayData[]>(() => [], []);
  const cptbDataDirect = useMemo<CptbData[]>(() => [], []);

  const allPreloadsFetched =
    stackedCsvQuery.status !== 'pending' &&
    spendEffectShareCsvQuery.status !== 'pending' &&
    isDecayPreloadSettled(
      runInputs.resolved,
      Boolean(runInputs.rawDecayKey),
      holisticDecayQuery.isSuccess
    ) &&
    holisticRcQuery.status !== 'pending' &&
    mddQuery.status !== 'pending';
  const loading = !allPreloadsFetched;

  useEffect(() => {
    if (roasData[0]) {
      setCentralMeasure(medianOrMean(roasData[0]));
    }
  }, [roasData]);

  const kpiType = useMemo(() => {
    const k = (stackedCsvPreload?.[0]?.model_kpi ?? '').toLowerCase();
    return k.includes('conversion') || k.includes('order') || k.includes('acquisition')
      ? 'conversions'
      : 'revenue';
  }, [stackedCsvPreload]);

  // Brand Equity's per-date predicted revenue, keyed by date -- used by the CPTB/Sankey covariate
  // residual (DEV-21662). Sourced from stackedCsvPreload, not the twostage decomposition data,
  // since predicted revenue isn't part of that dataset. Summed across same-date rows (e.g.
  // multiple geos), matching calculateModelTtmRevenueCredit's own aggregation.
  const beePredictedRevenueByDate = useMemo(() => {
    const map = new Map<string, number>();
    (stackedCsvPreload ?? []).forEach(row => {
      if (!isBrandEquityModelType(row.model_type) || row.metric !== MEAN_METRIC) {
        return;
      }
      map.set(row.date, (map.get(row.date) ?? 0) + (Number(row.predicted) || 0));
    });
    return map;
  }, [stackedCsvPreload, isBrandEquityModelType]);

  const normalizedWeightsMemo = useMemo(
    () =>
      calculateNormalizedWeightsUtil(
        models
          .filter(model => model.enabled)
          .map(model => ({ id: model.id, csvType: model.id, weight: model.weight }))
      ),
    [models]
  );

  // Slot-keyed weights over every enabled non-BE slot. Excludes Brand Equity: its paid-media
  // contribution to the CPTB/Sankey covariate residual comes from its Media-Driven Brand Equity
  // (MDBE) induced effect instead (DEV-21662) -- including its own `paid_attribution` too would
  // double-count.
  const stackedCsvNormalizedWeights = useMemo(
    () =>
      calculateNormalizedWeightsUtil(
        models
          .filter(model => model.enabled && !isBrandEquityModelType(model.id))
          .map(model => ({ id: model.id, csvType: model.id, weight: model.weight }))
      ),
    [models, isBrandEquityModelType]
  );

  // Weighted sum of every enabled non-BE model's paid_attribution, keyed by date -- see
  // stackedCsvNormalizedWeights above for why Brand Equity itself is excluded (DEV-21662).
  const paidAttributionByDate = useMemo(() => {
    const map = new Map<string, number>();
    (stackedCsvPreload ?? []).forEach(row => {
      const weight = stackedCsvNormalizedWeights.get(row.model_type);
      if (!weight || row.metric !== MEAN_METRIC) {
        return;
      }
      map.set(row.date, (map.get(row.date) ?? 0) + weight * (Number(row.paid_attribution) || 0));
    });
    return map;
  }, [stackedCsvPreload, stackedCsvNormalizedWeights]);

  const commonDateRange = useMemo((): { start: string; end: string } | null => {
    if (actualizedCsvData.length === 0 || enabledModelTypes.length === 0) {
      return null;
    }

    const datesByModelType: Record<string, Set<string>> = {};

    actualizedCsvData.forEach(row => {
      if (!enabledModelTypes.includes(row.model_type) || row.metric !== METRIC_MEAN) {
        return;
      }
      const rowDate = row.time || row.date || '';
      if (!rowDate) {
        return;
      }

      if (!datesByModelType[row.model_type]) {
        datesByModelType[row.model_type] = new Set();
      }
      datesByModelType[row.model_type].add(rowDate);
    });

    if (Object.keys(datesByModelType).length <= 1) {
      return null;
    }

    let commonStart = '';
    let commonEnd = '';
    let first = true;

    Object.values(datesByModelType).forEach(dates => {
      const sorted = Array.from(dates).sort();
      const modelStart = sorted[0];
      const modelEnd = sorted[sorted.length - 1];

      if (first) {
        commonStart = modelStart;
        commonEnd = modelEnd;
        first = false;
      } else {
        if (modelStart > commonStart) {
          commonStart = modelStart;
        }
        if (modelEnd < commonEnd) {
          commonEnd = modelEnd;
        }
      }
    });

    return commonStart && commonEnd && commonStart <= commonEnd
      ? { start: commonStart, end: commonEnd }
      : null;
  }, [enabledModelTypes, actualizedCsvData]);

  const processHolisticRcData = useCallback(
    (csvData: HolisticRcCsvRow[], isActualized: boolean): ProcessedHolisticGroup[] => {
      if (!csvData || csvData.length === 0) {
        return [];
      }

      const normalizedWeights = normalizedWeightsMemo;
      const filteredData = csvData.filter(row => enabledModelTypes.includes(row.model_type));

      const grouped = new Map<string, HolisticRcCsvRow[]>();

      filteredData.forEach(row => {
        const spendKey = isActualized ? row.actualized_spend : row.raw_spend;
        const groupKey = `${row.media_channel}__${row.estimate_type}__${spendKey}`;
        if (!grouped.has(groupKey)) {
          grouped.set(groupKey, []);
        }
        grouped.get(groupKey)!.push(row);
      });

      const processed: ProcessedHolisticGroup[] = [];

      grouped.forEach((rows, _groupKey) => {
        if (rows.length === 0) {
          return;
        }

        const firstRow = rows[0];

        let rawEffectSum = 0;
        let actualizedEffectSum = 0;
        let rawReturnNextDollarSum = 0;
        let actualizedReturnNextDollarSum = 0;
        let maxPreviousQtrMeanValue = 0;
        let maxActualizedSpend = 0;
        let maxRawSpend = 0;

        rows.forEach(row => {
          const weight = normalizedWeights.get(row.model_type) || 1.0;

          rawEffectSum += (Number(row.raw_effect) || 0) * weight;
          actualizedEffectSum += (Number(row.actualized_effect) || 0) * weight;
          rawReturnNextDollarSum += (Number(row.raw_return_next_dollar) || 0) * weight;
          actualizedReturnNextDollarSum +=
            (Number(row.actualized_return_next_dollar) || 0) * weight;
          maxPreviousQtrMeanValue = Math.max(
            maxPreviousQtrMeanValue,
            Number(row.previous_qtr_mean_value) || 0
          );
          maxActualizedSpend = Math.max(maxActualizedSpend, Number(row.actualized_spend) || 0);
          maxRawSpend = Math.max(maxRawSpend, Number(row.raw_spend) || 0);
        });

        processed.push({
          media_channel: firstRow.media_channel,
          estimate_type: firstRow.estimate_type,
          raw_spend: maxRawSpend,
          actualized_spend: isActualized ? maxActualizedSpend : firstRow.actualized_spend,
          raw_effect_sum: rawEffectSum,
          actualized_effect_sum: actualizedEffectSum,
          raw_return_next_dollar_sum: rawReturnNextDollarSum,
          actualized_return_next_dollar_sum: actualizedReturnNextDollarSum,
          previous_qtr_mean_value: maxPreviousQtrMeanValue,
          model_type: firstRow.model_type,
          dark_last_quarter: firstRow.dark_last_quarter
        });
      });

      return processed;
    },
    [enabledModelTypes, normalizedWeightsMemo]
  );

  const transformActualizedDataToCptb = useCallback(
    (csvData: ActualizedCsvRow[]): CptbData[] => {
      if (!csvData || csvData.length === 0) {
        return [];
      }

      // Run pipeline; spend correction is universal across the model toggle (D1).
      const { contributionRows, spendUniverse } = applyRoasPipeline(csvData, {
        enabledModelTypes
      });

      if (contributionRows.length === 0 && spendUniverse.length === 0) {
        return [];
      }

      const bucketKey = (
        bucketDate: string,
        channel: string,
        platform: string,
        tactic: string
      ): string =>
        `${bucketDate}${GROUP_KEY_SEPARATOR}${channel}${GROUP_KEY_SEPARATOR}${platform}${GROUP_KEY_SEPARATOR}${tactic}`;

      interface BucketSample {
        channel: string | undefined;
        model_type: string;
        refresh_date: string | undefined;
        parsed: ParsedMediaChannel;
      }
      interface Bucket {
        date: string;
        channel: string;
        platform: string;
        tactic: string;
        brand_nonbrand: string;
        sampleRow?: BucketSample;
        spend: number;
        effect: number;
        mostRecentRefreshDate: string;
      }
      const buckets = new Map<string, Bucket>();

      const ensureBucket = (
        key: string,
        bucketDate: string,
        channel: string,
        platform: string,
        tactic: string,
        brand_nonbrand: string
      ): Bucket => {
        const existing = buckets.get(key);
        if (existing) {
          return existing;
        }
        const created: Bucket = {
          date: bucketDate,
          channel,
          platform,
          tactic,
          brand_nonbrand,
          spend: 0,
          effect: 0,
          mostRecentRefreshDate: ''
        };
        buckets.set(key, created);
        return created;
      };

      // Universal aligned spend (toggle-invariant denominator).
      spendUniverse.forEach(entry => {
        const key = bucketKey(entry.date, entry.channel, entry.platform, entry.tactic);
        const b = ensureBucket(
          key,
          entry.date,
          entry.channel,
          entry.platform,
          entry.tactic,
          entry.brand_nonbrand
        );
        b.spend += entry.spend_corrected;
      });

      // Toggle-respecting contribution numerator + display metadata.
      contributionRows.forEach(row => {
        const parsed = parseChannelColumn(row.channel, row.media_channel);
        const rowDate = row.time || row.date || '';
        const channel = parsed.channel || 'Unknown';
        const platform = parsed.platform || '';
        const tactic = parsed.tactic || '';
        const key = bucketKey(rowDate, channel, platform, tactic);
        const b = ensureBucket(
          key,
          rowDate,
          channel,
          platform,
          tactic,
          parsed.brand_nonbrand || ''
        );
        b.effect += Number(row.actualized_effect) || 0;
        if (
          row.refresh_date &&
          (!b.mostRecentRefreshDate || row.refresh_date > b.mostRecentRefreshDate)
        ) {
          b.mostRecentRefreshDate = row.refresh_date;
        }
        if (!b.sampleRow) {
          b.sampleRow = {
            channel: row.channel,
            model_type: row.model_type,
            refresh_date: row.refresh_date,
            parsed
          };
        }
      });

      const transformed: CptbData[] = [];
      buckets.forEach(b => {
        const sample = b.sampleRow;
        const parsedFallback: ParsedMediaChannel = {
          channel: b.channel,
          platform: b.platform || undefined,
          tactic: b.tactic || undefined,
          brand_nonbrand: b.brand_nonbrand || undefined
        };
        const parsed = sample?.parsed ?? parsedFallback;
        const channelName = sample
          ? parseChannelName(sample.channel, isBrandEquityModelType(sample.model_type)) ||
            parsed.channel ||
            'Unknown'
          : parsed.channel || 'Unknown';
        const normalizedChannel = channelName.toLowerCase().trim();

        transformed.push({
          date: b.date,
          channel: normalizedChannel,
          platform: parsed.platform || null,
          tactic: parsed.tactic || null,
          brand_nonbrand: parsed.brand_nonbrand || null,
          name: channelName,
          raw_spend: b.spend,
          adjusted_outcome_to_observed: b.effect,
          predicted_outcome: b.effect,
          incrementality_test: false,
          refresh_date: b.mostRecentRefreshDate || sample?.refresh_date
        } as CptbData);
      });

      return transformed;
    },
    [enabledModelTypes, isBrandEquityModelType]
  );

  /**
   * Transform actualized spend/effect CSV to CompositionRow[] for RoasCompositionByRevenueStream.
   * Uses TTM (latest date - 52 weeks). Routes through applyRoasPipeline so the spend
   * denominator is universal across the model toggle (D1) and the per-cell ROAS uses
   * aligned spend (max across all available models per (date, name)).
   */
  const transformActualizedToCompositionRows = useCallback(
    (csvData: ActualizedCsvRow[]): { revenue: CompositionRow[]; roas: CompositionRow[] } => {
      if (!csvData || csvData.length === 0 || enabledModelTypes.length === 0) {
        return { revenue: [], roas: [] };
      }

      const weightByModelId: Record<string, number> = {};
      models
        .filter(m => m.enabled)
        .forEach(model => {
          weightByModelId[model.id] = normalizedWeightsMemo.get(model.id) ?? 1;
        });

      // TTM window from the universal-filtered set so the date scope is toggle-invariant (D1).
      const meanRows = csvData.filter(r => r.metric === METRIC_MEAN);
      let maxDate: Date | null = null;
      meanRows.forEach(r => {
        const s = r.time || r.date;
        if (!s) {
          return;
        }
        try {
          const d = Dfns.parseISO(s);
          if (!maxDate || d > maxDate) {
            maxDate = d;
          }
        } catch {
          /* no-op: unparseable dates don't contribute to the TTM window max date */
        }
      });
      if (!maxDate) {
        return { revenue: [], roas: [] };
      }
      const minDate = Dfns.subWeeks(TTM_WEEKS, maxDate);

      const inTTM = (row: ActualizedCsvRow): boolean => {
        const s = row.time || row.date;
        if (!s) {
          return false;
        }
        try {
          const d = Dfns.parseISO(s);
          return d >= minDate && d <= maxDate!;
        } catch {
          return false;
        }
      };

      // Pre-filter to TTM and exclude covariates (they have a separate "name") so they
      // don't pollute spend correction or the universe.
      const scopedRows = csvData.filter(row => {
        if (!inTTM(row)) {
          return false;
        }
        const parsed = parseChannelColumn(row.channel, row.media_channel);
        return (parsed.channel || '').toLowerCase() !== 'covariates';
      });

      const { contributionRows, spendUniverse } = applyRoasPipeline(scopedRows, {
        enabledModelTypes
      });

      if (contributionRows.length === 0 && spendUniverse.length === 0) {
        return { revenue: [], roas: [] };
      }

      const buildGroupKey = (parsed: ParsedMediaChannel): string => {
        const ch = parsed.channel || 'Unknown';
        const pl = parsed.platform || '';
        const ta = parsed.tactic || '';
        const br = parsed.brand_nonbrand || '';
        if (groupBy === 'channel') {
          return ch;
        }
        if (groupBy === 'platform') {
          if (!pl) {
            return ch;
          }
          return `${ch}${GROUP_KEY_SEPARATOR}${pl}`;
        }
        return `${ch}${GROUP_KEY_SEPARATOR}${pl}${GROUP_KEY_SEPARATOR}${ta}${GROUP_KEY_SEPARATOR}${br}`;
      };

      const buildGroupKeyFromUniverse = (entry: SpendUniverseEntry): string =>
        buildGroupKey({
          channel: entry.channel,
          platform: entry.platform,
          tactic: entry.tactic,
          brand_nonbrand: entry.brand_nonbrand
        });

      const buildDisplayLabel = (parsed: ParsedMediaChannel): string => {
        const ch = parsed.channel || 'Unknown';
        const pl = parsed.platform || '';
        const ta = parsed.tactic || '';
        const br = parsed.brand_nonbrand || '';
        if (groupBy === 'channel') {
          return capitalizeWords(ch, disabledPlatform);
        }
        if (groupBy === 'platform') {
          if (!pl) {
            return capitalizeWords(ch, disabledPlatform);
          }
          return `${capitalizeWords(ch, disabledPlatform)}, ${capitalizeWords(pl, disabledPlatform)}`;
        }
        const parts: string[] = [capitalizeWords(ch, disabledPlatform)];
        if (pl) {
          parts.push(capitalizeWords(pl, disabledPlatform));
        }
        if (ta) {
          parts.push(capitalizeWords(ta, disabledPlatform));
        }
        if (br) {
          parts.push(capitalizeWords(br, disabledPlatform));
        }
        return parts.join(', ');
      };

      const groupLabels: Record<string, string> = {};

      // Aligned spend per groupKey -- toggle-invariant denominator (D1).
      const spendByGroup: Record<string, number> = {};
      spendUniverse.forEach(entry => {
        const gk = buildGroupKeyFromUniverse(entry);
        spendByGroup[gk] = (spendByGroup[gk] ?? 0) + entry.spend_corrected;
        if (!(gk in groupLabels)) {
          groupLabels[gk] = buildDisplayLabel({
            channel: entry.channel,
            platform: entry.platform,
            tactic: entry.tactic,
            brand_nonbrand: entry.brand_nonbrand
          });
        }
      });

      // DEV-24605: a group is dark only when every contributing (pre-toggle) row is
      // dark_last_year -- toggle-invariant, computed from scopedRows (all model types)
      // so it doesn't shift when the model toggle changes.
      const darkByGroup: Record<string, boolean> = {};
      scopedRows.forEach(row => {
        const parsed = parseChannelColumn(row.channel, row.media_channel);
        const gk = buildGroupKey(parsed);
        const rowIsDark = Boolean(row.dark_last_year);
        darkByGroup[gk] = gk in darkByGroup ? darkByGroup[gk] && rowIsDark : rowIsDark;
      });

      // Per-(groupKey, modelId) contribution -- toggle-respecting numerator.
      const effectByGroupModel: Record<string, Record<string, number>> = {};
      contributionRows.forEach(row => {
        const parsed = parseChannelColumn(row.channel, row.media_channel);
        const gk = buildGroupKey(parsed);
        const modelId = row.model_type;
        if (!(gk in effectByGroupModel)) {
          effectByGroupModel[gk] = {};
        }
        effectByGroupModel[gk][modelId] =
          (effectByGroupModel[gk][modelId] ?? 0) + (Number(row.actualized_effect) || 0);
        if (!(gk in groupLabels)) {
          groupLabels[gk] = buildDisplayLabel(parsed);
        }
      });

      const allGroupKeys = new Set<string>([
        ...Object.keys(spendByGroup),
        ...Object.keys(effectByGroupModel)
      ]);

      const enabledModelIds = models.filter(m => m.enabled).map(m => m.id);

      const toRows = (useRoas: boolean): CompositionRow[] => {
        const rows: CompositionRow[] = [];
        const totalEffectByModel: Record<string, number> = {};
        let totalSpend = 0;

        // ROAS mode divides effect by aligned spend (0 when spend is 0); revenue mode uses the
        // provided non-ROAS value directly. Replaces the previous nested ternaries.
        const cellValue = (spend: number, effect: number, nonRoasValue: number): number => {
          if (!useRoas) {
            return nonRoasValue;
          }
          return spend > 0 ? effect / spend : 0;
        };

        allGroupKeys.forEach(gk => {
          const label = groupLabels[gk] ?? gk;
          // Per D1: every model cell within a row shares the same aligned-spend denominator.
          const rowSpend = spendByGroup[gk] ?? 0;
          const valuesByModel: Record<string, number> = {};
          const rowSpendByModel: Record<string, number> = {};
          const rowEffectByModel: Record<string, number> = {};
          let rowEffect = 0;

          enabledModelIds.forEach(mid => {
            const rawEffect = effectByGroupModel[gk]?.[mid] ?? 0;
            const effect = rawEffect * (weightByModelId[mid] ?? 1);
            rowEffect += effect;
            rowSpendByModel[mid] = rowSpend;
            rowEffectByModel[mid] = effect;
            valuesByModel[mid] = cellValue(rowSpend, effect, effect);
            totalEffectByModel[mid] = (totalEffectByModel[mid] ?? 0) + effect;
          });
          totalSpend += rowSpend;

          const rowTotal = cellValue(
            rowSpend,
            rowEffect,
            enabledModelIds.reduce((s, mid) => s + (valuesByModel[mid] ?? 0), 0)
          );
          rows.push({
            label,
            valuesByModel,
            rowTotal,
            rowSpendByModel,
            rowEffectByModel,
            darkLastYear: darkByGroup[gk] ?? false
          });
        });

        const overallValuesByModel: Record<string, number> = {};
        const overallSpendByModel: Record<string, number> = {};
        const overallEffectByModel: Record<string, number> = {};
        let overallEffect = 0;
        enabledModelIds.forEach(mid => {
          const effect = totalEffectByModel[mid] ?? 0;
          overallEffect += effect;
          overallSpendByModel[mid] = totalSpend;
          overallEffectByModel[mid] = effect;
          overallValuesByModel[mid] = cellValue(totalSpend, effect, effect);
        });
        const overallRowTotal = cellValue(
          totalSpend,
          overallEffect,
          enabledModelIds.reduce((s, mid) => s + (overallValuesByModel[mid] ?? 0), 0)
        );
        rows.push({
          label: 'Overall',
          valuesByModel: overallValuesByModel,
          rowTotal: overallRowTotal,
          rowSpendByModel: overallSpendByModel,
          rowEffectByModel: overallEffectByModel
        });
        return rows;
      };

      return { revenue: toRows(false), roas: toRows(true) };
    },
    [enabledModelTypes, groupBy, disabledPlatform, models, normalizedWeightsMemo]
  );

  /**
   * Get quarter date range based on Python logic:
   * - Groups by model_type, model_kpi, refresh_date
   * - Finds max date for each group
   * - Returns minimum of those max dates as quarter_max_date
   * - Returns quarter_max_date - 13 weeks as quarter_min_date
   */
  const getQuarterDateRange = useCallback(
    (
      csvData: ActualizedCsvRow[]
    ): {
      quarterMinDate: Date;
      quarterMaxDate: Date;
    } | null => {
      if (!csvData || csvData.length === 0) {
        return null;
      }

      const groupMaxDates = new Map<string, Date>();

      csvData.forEach(row => {
        const dateStr = row.time || row.date;
        if (!dateStr) {
          return;
        }

        try {
          const rowDate = Dfns.parseISO(dateStr);
          const groupKey = `${row.model_type}${GROUP_KEY_SEPARATOR}${
            row.model_kpi || ''
          }${GROUP_KEY_SEPARATOR}${row.refresh_date || ''}`;

          const existingMax = groupMaxDates.get(groupKey);
          if (!existingMax || rowDate > existingMax) {
            groupMaxDates.set(groupKey, rowDate);
          }
        } catch {
          /* no-op: unparseable dates are skipped when computing per-group max dates */
        }
      });

      if (groupMaxDates.size === 0) {
        return null;
      }

      const quarterMaxDate = Array.from(groupMaxDates.values()).reduce((min, current) =>
        current < min ? current : min
      );

      const quarterMinDate = Dfns.subWeeks(13, quarterMaxDate);

      return { quarterMinDate, quarterMaxDate };
    },
    []
  );

  const dateRangeFromActualized = useMemo(
    () => getQuarterDateRange(actualizedCsvData),
    [actualizedCsvData, getQuarterDateRange]
  );

  const compositionDataByOutcome = useMemo(
    () => transformActualizedToCompositionRows(actualizedCsvData),
    [actualizedCsvData, transformActualizedToCompositionRows]
  );

  // DEV-24605: "Omit Dark Inputs" for RoasCompositionByRevenueStream keys off
  // dark_last_year (TTM), not dark_last_quarter -- this container is already
  // TTM-scoped. Per Taurey, "Overall" always reflects every channel regardless
  // of the toggle -- only individual channel rows are hidden.
  const compositionDataByOutcomeToUse = useMemo(() => {
    if (!omitDarkInputs) {
      return compositionDataByOutcome;
    }
    const omitDark = (rows: CompositionRow[]) =>
      rows.filter(row => row.label === 'Overall' || !row.darkLastYear);
    return {
      revenue: omitDark(compositionDataByOutcome.revenue),
      roas: omitDark(compositionDataByOutcome.roas)
    };
  }, [omitDarkInputs, compositionDataByOutcome]);

  const normalizeChannelName = useCallback((channel: string | undefined | null): string => {
    if (!channel) {
      return '';
    }
    return channel
      .toLowerCase()
      .replace(/[_\s,]/g, '')
      .trim();
  }, []);

  /** First row per normalized channel key; used when actualizedByMediaChannel has no match (see findMatchingCsvRowByChannel) */
  const actualizedByNormalizedChannel = useMemo(() => {
    const m = new Map<string, ActualizedCsvRow>();
    actualizedCsvData.forEach(row => {
      const key = normalizeChannelName(
        parseChannelName(row.channel, isBrandEquityModelType(row.model_type)) || row.channel || ''
      );
      if (key && !m.has(key)) {
        m.set(key, row);
      }
    });
    return m;
  }, [actualizedCsvData, isBrandEquityModelType, normalizeChannelName]);

  const findMatchingCsvRowByChannel = useCallback(
    (
      csvRows: ActualizedCsvRow[],
      parsedChannel: ParsedMediaChannel,
      modelType: string
    ): ActualizedCsvRow | undefined => {
      const targetChannelName =
        parseChannelName(parsedChannel.channel, isBrandEquityModelType(modelType)) ||
        parsedChannel.channel ||
        '';
      const normalizedTarget = normalizeChannelName(targetChannelName);

      return csvRows.find(row => {
        if (!row.channel) {
          return false;
        }
        const csvChannelFirstPart = row.channel.split(',')[0]?.trim() || row.channel;
        const normalizedCsvChannel = normalizeChannelName(csvChannelFirstPart);
        return normalizedCsvChannel === normalizedTarget;
      });
    },
    [isBrandEquityModelType, normalizeChannelName]
  );

  const calculateRoasFromActualizedData = useCallback(
    (
      csvData: ActualizedCsvRow[],
      channel: string,
      isPreviousQuarter: boolean,
      normalizedWeights: Map<string, number>,
      dateRangeOverride: { quarterMinDate: Date; quarterMaxDate: Date } | null | undefined,
      fallbackSpend: number | null = null
    ): { roas: number | null; cpa: number | null } => {
      if (!csvData || csvData.length === 0) {
        return { roas: null, cpa: null };
      }

      const dateRange = dateRangeOverride ?? getQuarterDateRange(csvData);
      if (!dateRange) {
        return { roas: null, cpa: null };
      }

      const { quarterMinDate, quarterMaxDate } = dateRange;

      const baseFilteredData = csvData.filter(row => {
        if (row.metric !== METRIC_MEAN) {
          return false;
        }

        if (!row.channel || row.channel !== channel) {
          return false;
        }

        const dateStr = row.time || row.date;
        if (!dateStr) {
          return false;
        }

        try {
          const rowDate = Dfns.parseISO(dateStr);
          if (isPreviousQuarter) {
            if (rowDate < quarterMinDate || rowDate > quarterMaxDate) {
              return false;
            }
          } else if (rowDate > quarterMaxDate) {
            return false;
          }
        } catch {
          return false;
        }

        return true;
      });

      if (baseFilteredData.length === 0) {
        return { roas: null, cpa: null };
      }

      const spendData = baseFilteredData.filter(row => rmmmSlotIds.includes(row.model_type));
      const spends: number[] = spendData.map(row => Number(row.actualized_spend) || 0);

      let meanSpend: number | null = null;
      if (spends.length > 0) {
        const sum = spends.reduce((acc, val) => acc + val, 0);
        meanSpend = sum / spends.length;
      }

      const effectByChannelAndTime = new Map<string, number>();

      baseFilteredData.forEach(row => {
        const dateStr = row.time || row.date;
        if (!dateStr) {
          return;
        }

        const key = `${channel}${GROUP_KEY_SEPARATOR}${dateStr}`;
        const effect = Number(row.actualized_effect) || 0;
        const currentSum = effectByChannelAndTime.get(key) || 0;
        effectByChannelAndTime.set(key, currentSum + effect);
      });

      const responsesByChannel: number[] = [];
      const channelPrefix = `${channel}${GROUP_KEY_SEPARATOR}`;
      effectByChannelAndTime.forEach((summedEffect, key) => {
        if (key.startsWith(channelPrefix)) {
          responsesByChannel.push(summedEffect);
        }
      });

      let meanResponse: number | null = null;
      if (responsesByChannel.length > 0) {
        const sum = responsesByChannel.reduce((acc, val) => acc + val, 0);
        meanResponse = sum / responsesByChannel.length;
      }

      const effectiveSpend =
        (meanSpend === null || meanSpend === 0) &&
        isPreviousQuarter &&
        fallbackSpend !== null &&
        fallbackSpend > 0
          ? fallbackSpend
          : meanSpend;

      let roas: number | null = null;
      let cpa: number | null = null;

      if (effectiveSpend !== null && effectiveSpend > 0 && meanResponse !== null) {
        roas = meanResponse / effectiveSpend;
        if (Number.isNaN(roas) || !Number.isFinite(roas)) {
          roas = null;
        }
      }

      if (
        meanResponse !== null &&
        meanResponse > 0 &&
        effectiveSpend !== null &&
        effectiveSpend > 0
      ) {
        cpa = effectiveSpend / meanResponse;
        if (Number.isNaN(cpa) || !Number.isFinite(cpa)) {
          cpa = null;
        }
      }

      return { roas, cpa };
    },
    [getQuarterDateRange, rmmmSlotIds]
  );

  const transformHolisticToRoasData = useCallback(
    (
      processedData: ProcessedHolisticGroup[],
      isActualized: boolean,
      centralMeasureArg: MedianOrMean
    ): RoasData[] => {
      if (!processedData || processedData.length === 0) {
        return [];
      }

      const transformed: RoasData[] = [];
      const mediaChannelGroups = new Map<string, ProcessedHolisticGroup[]>();

      const meanData = processedData.filter(g => g.estimate_type === METRIC_MEAN);

      meanData.forEach(group => {
        if (!mediaChannelGroups.has(group.media_channel)) {
          mediaChannelGroups.set(group.media_channel, []);
        }
        mediaChannelGroups.get(group.media_channel)!.push(group);
      });

      mediaChannelGroups.forEach((groups, mediaChannel) => {
        const previousQtrMeanGroup = groups.find(g => g.previous_qtr_mean_value === 1);

        const meanGroupForNextDollar =
          previousQtrMeanGroup ||
          groups.reduce((prev, curr) => {
            const prevSpend = isActualized ? prev.actualized_spend : prev.raw_spend;
            const currSpend = isActualized ? curr.actualized_spend : curr.raw_spend;
            return currSpend > prevSpend ? curr : prev;
          });

        const isNewFormat = !mediaChannel?.includes('__');
        const parsed = parseChannelColumn(isNewFormat ? mediaChannel : undefined, mediaChannel);
        const modelType = meanGroupForNextDollar.model_type;
        const channelName =
          parseChannelName(parsed.channel, isBrandEquityModelType(modelType)) ||
          parsed.channel ||
          'Unknown';

        const nameParts = [
          parsed.channel,
          parsed.platform,
          parsed.tactic,
          parsed.brand_nonbrand
        ].filter(Boolean);
        const name = nameParts.length > 0 ? nameParts.join(', ') : channelName;

        const nextDollarEffectiveness = isActualized
          ? meanGroupForNextDollar.actualized_return_next_dollar_sum
          : meanGroupForNextDollar.raw_return_next_dollar_sum;

        let previousQtrSpend: number | null = null;
        if (previousQtrMeanGroup) {
          previousQtrSpend = isActualized
            ? previousQtrMeanGroup.actualized_spend
            : previousQtrMeanGroup.raw_spend;
        }

        let responsePreviousQtrSpend: number | null = null;
        if (previousQtrMeanGroup) {
          responsePreviousQtrSpend = isActualized
            ? previousQtrMeanGroup.actualized_effect_sum
            : previousQtrMeanGroup.raw_effect_sum;
        }

        let csvRow = actualizedByMediaChannel.get(mediaChannel)?.[0];
        if (!csvRow) {
          const normalizedKey = normalizeChannelName(
            parseChannelName(parsed.channel, isBrandEquityModelType(modelType)) ||
              parsed.channel ||
              ''
          );
          csvRow =
            actualizedByNormalizedChannel.get(normalizedKey) ??
            findMatchingCsvRowByChannel(actualizedCsvData, parsed, modelType);
        }

        const channelForCalculation = csvRow?.channel || channelName;
        const channelRows = actualizedByChannel.get(channelForCalculation) ?? [];

        const previousQtrRoasCpa = calculateRoasFromActualizedData(
          channelRows,
          channelForCalculation,
          true,
          normalizedWeightsMemo,
          dateRangeFromActualized,
          previousQtrSpend
        );
        const allTimeRoasCpa = calculateRoasFromActualizedData(
          channelRows,
          channelForCalculation,
          false,
          normalizedWeightsMemo,
          dateRangeFromActualized
        );

        const csvDarkFlag = meanGroupForNextDollar.dark_last_quarter;
        const darkLastQuarter =
          csvDarkFlag !== undefined
            ? csvDarkFlag === 1
            : previousQtrSpend !== null && previousQtrSpend < DARK_INPUT_SPEND_THRESHOLD;

        const roasDataItem: Partial<RoasData> = {
          name,
          channel: parsed.channel || undefined,
          platform: parsed.platform || undefined,
          tactic: parsed.tactic || undefined,
          brand_nonbrand: parsed.brand_nonbrand || undefined,
          next_dollar_effectiveness: nextDollarEffectiveness ?? undefined,
          previous_qtr_roas: previousQtrRoasCpa.roas ?? undefined,
          all_time_roas: allTimeRoasCpa.roas ?? undefined,
          previous_qtr_cpa: previousQtrRoasCpa.cpa ?? undefined,
          all_time_cpa: allTimeRoasCpa.cpa ?? undefined,
          incrementality_test: false,
          dark_last_quarter: darkLastQuarter ? 1 : 0
        };

        if (centralMeasureArg === MedianOrMean.MEDIAN) {
          roasDataItem.previous_qtr_median_spend = previousQtrSpend ?? undefined;
          roasDataItem.median = previousQtrMeanGroup ? 1 : 0;
          roasDataItem.mean = 0;
        } else {
          roasDataItem.previous_qtr_mean_spend = previousQtrSpend ?? undefined;
          roasDataItem.mean = previousQtrMeanGroup ? 1 : 0;
          roasDataItem.median = 0;
        }

        const hasPrevQtrSpend = previousQtrSpend !== null && previousQtrSpend > 0;
        roasDataItem.response_previous_qtr_median_spend = hasPrevQtrSpend
          ? (responsePreviousQtrSpend ?? undefined)
          : undefined;
        if (centralMeasureArg === MedianOrMean.MEAN) {
          roasDataItem.response_previous_qtr_mean_spend = hasPrevQtrSpend
            ? (responsePreviousQtrSpend ?? undefined)
            : undefined;
        }

        transformed.push(roasDataItem as RoasData);
      });

      return transformed;
    },
    [
      calculateRoasFromActualizedData,
      actualizedCsvData,
      findMatchingCsvRowByChannel,
      actualizedByMediaChannel,
      actualizedByChannel,
      actualizedByNormalizedChannel,
      normalizedWeightsMemo,
      dateRangeFromActualized,
      normalizeChannelName,
      isBrandEquityModelType
    ]
  );

  // twoStageData feeds BOTH the CPTB "Actualized" bar chart and the Dynamics sankey modal (same
  // prop/data — see TwoStageSpendandEffectContainer/SpendAndEffectModal).
  useEffect(() => {
    setTwoStageData(twostageDecompositionQuery.data ?? []);
  }, [twostageDecompositionQuery.data]);

  useEffect(() => {
    if (actualizedCsvData.length > 0 && enabledModelTypes.length > 0) {
      const transformedCptb = transformActualizedDataToCptb(actualizedCsvData);
      setCptbData(transformedCptb);
      setGroupByOptions(findGroupByOptions(transformedCptb));
    } else if (enabledModelTypes.length === 0) {
      setCptbData([]);
    }
  }, [actualizedCsvData, enabledModelTypes, setGroupByOptions, transformActualizedDataToCptb]);

  useEffect(() => {
    if (enabledModelTypes.length === 0) {
      setRoasData([]);
      setRoasDataDirect([]);
      setSatData([]);
      setSatDataDirect([]);
      return;
    }

    if (holisticRcData.length > 0 && actualizedCsvData.length > 0) {
      const processedActualized = processHolisticRcData(holisticRcData, true);
      const transformedActualized = transformHolisticToRoasData(
        processedActualized,
        true,
        centralMeasure
      );
      if (transformedActualized.length > 0) {
        setRoasData(transformedActualized);
      }

      const processedRaw = processHolisticRcData(holisticRcData, false);
      const transformedRaw = transformHolisticToRoasData(processedRaw, false, centralMeasure);
      if (transformedRaw.length > 0) {
        setRoasDataDirect(transformedRaw);
      }

      const transformedSatData = transformHolisticRcToSatData(
        holisticRcData,
        enabledModelTypes,
        normalizedWeightsMemo,
        true, // Always use actualized view
        kpi,
        company,
        refreshDate
      );
      if (transformedSatData.length > 0) {
        setSatData(transformedSatData);
        // set satDataDirect to the same data since we're always using actualized
        setSatDataDirect(transformedSatData);
      }
    }
  }, [
    holisticRcData,
    enabledModelTypes,
    centralMeasure,
    processHolisticRcData,
    transformHolisticToRoasData,
    normalizedWeightsMemo,
    kpi,
    company,
    refreshDate,
    actualizedCsvData
  ]);

  const derivedHolisticSatData = useMemo(() => {
    if (
      holisticRcData.length === 0 ||
      enabledModelTypes.length === 0 ||
      actualizedCsvData.length === 0
    ) {
      return [];
    }
    return transformHolisticRcToSatData(
      holisticRcData,
      enabledModelTypes,
      normalizedWeightsMemo,
      true,
      kpi,
      company,
      refreshDate
    );
  }, [
    holisticRcData,
    enabledModelTypes,
    actualizedCsvData,
    normalizedWeightsMemo,
    refreshDate,
    kpi,
    company
  ]);

  const mddRoasData = useMemo(
    () =>
      mddQuery.data && mddQuery.data.length > 0
        ? adaptMddRowsToRoasData(
            mddQuery.data,
            enabledModelTypes,
            normalizedWeightsMemo,
            centralMeasure
          )
        : null,
    [mddQuery.data, enabledModelTypes, normalizedWeightsMemo, centralMeasure]
  );

  const renderOmitDarkInputsToggle = useMemo(() => {
    const hasDark = (rows: { dark_last_quarter?: number }[]) =>
      rows.reduce((acc, row) => Boolean(row.dark_last_quarter || acc), false);
    return (
      hasDark(mddRoasData ?? roasData) ||
      hasDark(roasDataDirect) ||
      hasDark(satData) ||
      hasDark(satDataDirect) ||
      hasDark(decayData) ||
      hasDark(holisticDecayDataPreload ?? []) ||
      actualizedCsvData.some(row => Boolean(row.dark_last_year))
    );
  }, [
    decayData,
    holisticDecayDataPreload,
    mddRoasData,
    roasData,
    roasDataDirect,
    satData,
    satDataDirect,
    actualizedCsvData
  ]);

  const filteredRoasData = useMemo(
    () => roasData.filter(row => !row.dark_last_quarter),
    [roasData]
  );

  const filteredRoasDataDirect = useMemo(
    () => roasDataDirect.filter(row => !row.dark_last_quarter),
    [roasDataDirect]
  );

  const roasDataToUse = useMemo(() => {
    if (mddRoasData && mddRoasData.length > 0) {
      return omitDarkInputs ? mddRoasData.filter(r => !r.dark_last_quarter) : mddRoasData;
    }
    if (showActualizedData) {
      return omitDarkInputs ? filteredRoasData : roasData;
    }
    return omitDarkInputs ? filteredRoasDataDirect : roasDataDirect;
  }, [
    mddRoasData,
    filteredRoasData,
    filteredRoasDataDirect,
    omitDarkInputs,
    roasData,
    roasDataDirect,
    showActualizedData
  ]);

  const filteredSatData = useMemo(() => satData.filter(row => !row.dark_last_quarter), [satData]);

  const filteredSatDataDirect = useMemo(
    () => satDataDirect.filter(row => !row.dark_last_quarter),
    [satDataDirect]
  );

  const satDataToUse = useMemo(() => {
    if (showActualizedData) {
      return omitDarkInputs ? filteredSatData : satData;
    }
    return omitDarkInputs ? filteredSatDataDirect : satDataDirect;
  }, [
    filteredSatData,
    filteredSatDataDirect,
    omitDarkInputs,
    satData,
    satDataDirect,
    showActualizedData
  ]);

  const filteredDerivedHolisticSatData = useMemo(
    () => derivedHolisticSatData.filter(row => !row.dark_last_quarter),
    [derivedHolisticSatData]
  );

  const satDataForChart = useMemo(() => {
    if (derivedHolisticSatData.length > 0) {
      return omitDarkInputs ? filteredDerivedHolisticSatData : derivedHolisticSatData;
    }
    return satDataToUse;
  }, [derivedHolisticSatData, omitDarkInputs, filteredDerivedHolisticSatData, satDataToUse]);

  const filteredDecayData = useMemo(
    () => decayData.filter(row => !row.dark_last_quarter),
    [decayData]
  );

  const decayDataToUse = useMemo(
    () => (omitDarkInputs ? filteredDecayData : decayData),
    [decayData, filteredDecayData, omitDarkInputs]
  );

  if (enabledModelTypes.length === 0) {
    return (
      <div className='hroas-no-models-enabled'>
        <p>Select at least one model to view results.</p>
      </div>
    );
  }

  return (
    <>
      {!loading ? (
        <div>
          <ModelOverview
            company={company}
            disabledPlatform={disabledPlatform}
            groupBy={groupBy}
            kpi={kpi}
            kpiType={kpiType}
            overallRoasCpa={overallRoasCpa}
            actualVsPredictedData={actualVsPredictedData}
            models={models}
            slots={slots}
            onModelsChange={onModelsChange}
            toolbarOutcomeVariable={toolbarOutcomeVariable}
            stackedCsvDataPreloaded={stackedCsvPreload ?? undefined}
            spendEffectShareCsvRows={spendEffectShareCsvQuery.data ?? null}
            compositionDataRoas={compositionDataByOutcome.roas}
          />
          <CPTB
            kpi={kpi}
            cptbData={showActualizedData ? cptbData : cptbDataDirect}
            disabledPlatform={disabledPlatform}
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            groupByOptions={groupByOptions}
            kpiType={kpiType}
            setOverallRoasCpa={setOverallRoasCpa}
            showActualizedData={showActualizedData}
            twoStageData={twoStageData}
            modelDate={date}
            commonDateRange={commonDateRange}
            isBrandEquityEnabled={enabledModelTypes.some(isBrandEquityModelType)}
            beePredictedRevenueByDate={beePredictedRevenueByDate}
            paidAttributionByDate={paidAttributionByDate}
          />
          <RoasCompositionByRevenueStream
            groupBy={groupBy}
            groupByOptions={groupByOptions}
            toolbarOutcomeVariable={toolbarOutcomeVariable}
            models={models}
            compositionDataRevenue={compositionDataByOutcomeToUse.revenue}
            compositionDataRoas={compositionDataByOutcomeToUse.roas}
            disabledPlatform={disabledPlatform}
            renderOmitDarkInputsToggle={renderOmitDarkInputsToggle}
            omitDarkInputs={omitDarkInputs}
            setOmitDarkInputs={setOmitDarkInputs}
          />
          <ReturnOnAdSpend
            disabledPlatform={disabledPlatform}
            kpiType={kpiType}
            data={roasDataToUse}
            renderOmitDarkInputsToggle={renderOmitDarkInputsToggle}
            omitDarkInputs={omitDarkInputs}
            setOmitDarkInputs={setOmitDarkInputs}
            centralMeasure={centralMeasure}
          />
          <SatAndDecay
            disabledPlatform={disabledPlatform}
            kpiType={kpiType}
            satData={satDataForChart}
            decayData={decayDataToUse}
            renderOmitDarkInputsToggle={renderOmitDarkInputsToggle}
            omitDarkInputs={omitDarkInputs}
            setOmitDarkInput={setOmitDarkInputs}
            centralMeasure={centralMeasure}
            enabledModelTypes={enabledModelTypes}
            modelLabels={modelLabels}
            normalizedWeights={normalizedWeightsMemo}
            holisticDecayDataPreloaded={holisticDecayDataPreload ?? undefined}
            decayStructurePreloaded={decayStructurePreload ?? undefined}
            isDecayRefetching={holisticDecayQuery.isFetching}
          />
        </div>
      ) : (
        <FullPageSpinner />
      )}
    </>
  );
};
